import type { DatumEntity } from "building-types/datums/datumEntityTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { DocEntityHandle, GroupableEntityHandle, GroupHandle } from "building-types/handles/docEntityHandleTypes";
import type { GroupableEntityType } from "building-types/handles/docEntityHandleTypes";
import type { SettingsSet } from "building-types/settings/settingsSet";
import type { ContentTemplate } from "building-types/views/planSets";
import type { RWTable, Table } from "common/data/table";
import objectMap from "common/utils/objectMap";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { docEntityHandleHash } from "./core/handleHash";
import docEntityGetParent from "./docEntityGetParent";
import type { GroupableEntity } from "./docEntityGroupable";
import { groupableEntityTypes, isGroupableEntityBySubType } from "./docEntityGroupable";
import { docGetEntityHandleByHash } from "./docEntityHandleHash";
import { docGetEntityGroupId } from "./docGetEntityGroupId";
import { docGetEntityGroup } from "./docGetGroup";
import { docGetTableEntity } from "./docGetTableEntity";
import type { EntityGroupSet } from "./entityGroupSet";

// This list is used to determine which entity types are sortable in the GroupInspector.

type GroupableAndSortableEntityType = Extract<
  GroupableEntityHandle,
  {
    type:
      | "OPENING"
      | "PROP_INSTANCE"
      | "CASEWORK"
      | "MODIFIER"
      | "FACADE"
      | "ELECTRICAL_DEVICE"
      | "SETTINGS_SET"
      | "ROOF_SYSTEM"
      | "CONTENT_TEMPLATE"
      | "DATUM"
      | "PROP"
      | "FOUNDATION_ASSEMBLY"
      | "FOUNDATION_COMPONENT"
      | "FRAME_FLOOR_ASSEMBLY"
      | "FRAMING_COMPONENT"
      | "CIP_COMPONENT"
      | "MASONRY_COMPONENT"
      | "SHEATHING_COMPONENT"
      | "SLAB_COMPONENT"
      | "SLAB_FLOOR_ASSEMBLY"
      | "WALL_PLANE_ASSEMBLY"
      | "PROFILE";
  }
>["type"];

export const groupableAndSortableEntityTypes: {
  [key in GroupableAndSortableEntityType]: boolean | ((entity: DocEntityTypeToValueType[key]) => boolean);
} = {
  OPENING: true,
  PROP_INSTANCE: true,
  CASEWORK: true,
  MODIFIER: true,
  ROOF_SYSTEM: true,
  FACADE: true,
  ELECTRICAL_DEVICE: true,
  CONTENT_TEMPLATE: true,
  SETTINGS_SET: true,
  DATUM: true,
  PROP: true,
  FOUNDATION_ASSEMBLY: true,
  FOUNDATION_COMPONENT: true,
  FRAME_FLOOR_ASSEMBLY: true,
  FRAMING_COMPONENT: true,
  CIP_COMPONENT: true,
  MASONRY_COMPONENT: true,
  SHEATHING_COMPONENT: true,
  SLAB_COMPONENT: true,
  SLAB_FLOOR_ASSEMBLY: true,
  WALL_PLANE_ASSEMBLY: true,
  PROFILE: true,
};

export function getGroupsByEntityType<T extends GroupableEntityType>(
  entities: DocEntityBase<T>[],
  entityGroups: RWTable<string>,
  deriveEntityHandle: (entity: DocEntityBase<T>) => GroupableEntityHandle | undefined,
): [RWTable<DocEntityBase<T>>, EntityGroupSet<T>] {
  const groups: EntityGroupSet<T> = {};
  const nonGroups: RWTable<DocEntityBase<T>> = {};

  for (const entity of entities) {
    const handle = deriveEntityHandle(entity as any);

    if (!handle) {
      continue;
    }

    const groupId = docGetEntityGroupId({ entityGroups }, handle);

    if (groupId) {
      if (groups[groupId]) {
        groups[groupId].push(entity);
      } else {
        groups[groupId] = [entity];
      }
    } else {
      nonGroups[entity.id] = entity;
    }
  }

  return [nonGroups, objectMap(groups, (group) => sortEntitiesBySortIndex(group))];
}

export function docEntityGroupGetMemberSiblings(
  doc: Doc,
  handle: GroupableEntityHandle,
  predicate: (entity: GroupableEntity) => boolean = () => true,
): GroupableEntityHandle[] {
  const hash = docEntityHandleHash(handle);
  const groupId = doc.entityGroups[hash];

  if (!groupId) {
    return [];
  }

  const groupSiblings: GroupableEntityHandle[] = [];

  for (const hash in doc.entityGroups) {
    const isSibling = doc.entityGroups[hash] === groupId;

    if (!isSibling) {
      continue;
    }

    const handle = docGetEntityHandleByHash(doc, hash) as GroupableEntityHandle;
    const sibling = docGetTableEntity(doc, handle);

    if (!sibling) {
      continue;
    }

    if (predicate(sibling)) {
      groupSiblings.push(handle);
    }
  }

  return groupSiblings;
}

export function docEntityGetGroup(doc: Doc, handle: DocEntityHandle): GroupHandle | null {
  const groupId = doc.entityGroups[docEntityHandleHash(handle)];
  if (groupId) {
    return { type: "GROUP", id: groupId };
  }

  return null;
}

export function docEntityGetParentGroup(doc: Doc, handle: DocEntityHandle): DocEntityHandle | undefined {
  const groupHandle = docEntityGetGroup(doc, handle);

  if (groupHandle) {
    return groupHandle;
  }

  return docEntityGetParent(doc, handle);
}

export function docExpandEntityGroupsWithSelectedChildren(
  doc: Doc,
  selectedHandles: DocEntityHandle[],
): Table<boolean> {
  const expandedEntityGroups: RWTable<boolean> = {};

  for (const handle of selectedHandles) {
    switch (handle.type) {
      // expand the group if a room is selected within a grouped block
      case "ROOM": {
        const room = docGetTableEntity(doc, handle);

        if (!room?.parentId) {
          continue;
        }

        const blockHandle: DocEntityHandle | null = room ? { type: "BLOCK", id: room.parentId } : null;

        if (!blockHandle) {
          continue;
        }

        const blockHandleHash = docEntityHandleHash(blockHandle);
        const blockGroupId = doc.entityGroups[blockHandleHash];
        if (!blockGroupId) {
          continue;
        }

        const blockGroupHash = docEntityHandleHash({ type: "GROUP", id: blockGroupId });
        expandedEntityGroups[blockGroupHash] = true;
        break;
      }

      case "ROOF": {
        const roof = docGetTableEntity(doc, handle);
        const roofSystemHandle: DocEntityHandle | null = roof ? { type: "ROOF_SYSTEM", id: roof.parentId } : null;

        if (!roofSystemHandle) {
          continue;
        }

        const roofSystemHandleHash = docEntityHandleHash(roofSystemHandle);
        const roofSystemGroupId = doc.entityGroups[roofSystemHandleHash];

        if (roofSystemGroupId) {
          const roofSystemGroupHash = docEntityHandleHash({ type: "GROUP", id: roofSystemGroupId });
          expandedEntityGroups[roofSystemGroupHash] = true;
        }

        break;
      }

      case "STAIR_BLOCK": {
        const stairBlock = docGetTableEntity(doc, handle);
        const stairSystemHandle: DocEntityHandle | null = stairBlock
          ? { type: "STAIR_SYSTEM", id: stairBlock.stairSystemId }
          : null;

        if (!stairSystemHandle) {
          continue;
        }

        const stairSystemHandleHash = docEntityHandleHash(stairSystemHandle);
        const stairSystemGroupId = doc.entityGroups[stairSystemHandleHash];

        if (stairSystemGroupId) {
          const stairSystemGroupHash = docEntityHandleHash({ type: "GROUP", id: stairSystemGroupId });
          expandedEntityGroups[stairSystemGroupHash] = true;
        }
        break;
      }

      case "STAIR_SYSTEM":
      case "VIEW_3D":
      case "PLAN_VIEW":
      case "ELEVATION_VIEW":
      case "ROOF_SYSTEM":
      case "BLOCK":
      case "OPENING":
      case "FLOOR":
      case "FACADE":
      case "ROOM_LAYOUT":
      case "SETTINGS":
      case "SETTINGS_SET":
      case "SHAPE":
      case "REFERENCE_GEOM":
      case "COLUMN":
      case "RAILING":
      case "ANNOTATION": {
        const hash = docEntityHandleHash(handle);
        const groupId = doc.entityGroups[hash];
        if (!groupId) {
          continue;
        }

        const groupHash = docEntityHandleHash({ type: "GROUP", id: groupId });
        expandedEntityGroups[groupHash] = true;
      }
      default: {
        continue;
      }
    }
  }

  return expandedEntityGroups;
}

export function docEntityIsEligibleForGroup(
  doc: Doc,
  entity: GroupableEntityHandle,
  children: GroupableEntityHandle[],
) {
  const groupEligible = groupableEntityTypes[entity.type];
  const alreadyGrouped = docGetEntityGroup(doc, entity);

  if (!groupEligible || alreadyGrouped || !children.length) {
    return false;
  }

  if (isGroupableEntityBySubType(entity.type)) {
    switch (entity.type) {
      case "SETTINGS_SET": {
        const settingsSet = docGetTableEntity(doc, entity);

        if (!settingsSet) {
          return false;
        }

        const childrenSettingsSets = children
          .map((handle) => docGetTableEntity(doc, handle))
          .filter(Boolean) as SettingsSet[];

        // Ensure all children settings sets are of the same behavior type
        return childrenSettingsSets.every((child) => child.type === settingsSet.type);
      }
      case "CONTENT_TEMPLATE": {
        const contentTemplate = docGetTableEntity(doc, entity);

        if (!contentTemplate) {
          return false;
        }

        const childrenContentTemplates = children
          .map((handle) => docGetTableEntity(doc, handle))
          .filter(Boolean) as ContentTemplate[];

        // Ensure all children content templates are of the same template type
        return childrenContentTemplates.every((child) => child.templateType === contentTemplate.templateType);
      }

      case "DATUM": {
        const datum = docGetTableEntity(doc, entity);

        if (!datum) {
          return false;
        }

        const childrenDatums = children
          .map((handle) => docGetTableEntity(doc, handle))
          .filter(Boolean) as DatumEntity[];

        return childrenDatums.every((child) => child.datumType === datum.datumType);
      }
    }
  }

  // Ensure all of the group's children are of the same type
  return children.every((child) => child.type === entity.type);
}
